import React, { useEffect, useState } from "react";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { GenericInput } from "../../../Shared/components/GenericInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { IVehicle } from "../../Vehicle/models/Vehicle";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CreateApprovalInput } from "../models/CreateApprovalInput";
import { ApprovalService } from "../services/approval.service";
import { IApproval } from "../models/Approval";
import { AuthService } from "../../Auth/services/AuthService";
import { useTranslation } from "react-i18next";

type ApprovalFormProps = ModalProps & {
  company?: IVehicle;
  approval?: IApproval;
};

export const ApprovalForm: React.FC<ApprovalFormProps> = ({
  onClose,
  company,
  approval,
}) => {
  const {t} = useTranslation()
  const message = t('loading_message');

  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const isAllowed = AuthService.isManagementAllowed();

  const currentDate = new Date().toISOString()
  const nextYearDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString();

  const [agreedTripNumber, setAgreedTripNumber] = useState(
    approval?.agreedTripNumber || ""
  );
  const [issueDate, setIssueDate] = useState((approval?.issueDate ?? currentDate) || "");
  const [validFrom, setValidFrom] = useState((approval?.validFrom ?? currentDate) || "");
  const [validUntil, setValidUntil] = useState((approval?.validUntil ?? nextYearDate) || "");
  const [issueAddress, setIssueAddress] = useState(
    approval?.issueAddress || ""
  );
  const [authorizedRoute, setAuthorizedRoute] = useState(
    approval?.authorizedRoute || ""
  );
  const [authorizedGoods, setAuthorizedGoods] = useState(
    approval?.authorizedGoods || ""
  );

  const onAgreedTripNumber = (val: string) => setAgreedTripNumber(val);
  const onIssueDate = (val: string) => setIssueDate(val);
  const onValidFrom = (val: string) => setValidFrom(val);
  const onValidUntil = (val: string) => setValidUntil(val);
  const onIssueAddress = (val: string) => setIssueAddress(val);
  const onAuthorizedRoute = (val: string) => setAuthorizedRoute(val);
  const onAuthorizedGoods = (val: string) => setAuthorizedGoods(val);

  const resetForm = () => {
    setAgreedTripNumber("");
    setIssueDate("");
    setValidFrom("");
    setValidUntil("");
    setIssueAddress("");
    setAuthorizedRoute("");
    setAuthorizedGoods("");
  };
  const isFormValid = authorizedRoute.trim() && authorizedGoods.trim();

  const createAction = async (e: React.FormEvent) => {
    e.preventDefault();

    const createApprovalInput = new CreateApprovalInput();
    if (approval?.id) {
      createApprovalInput.id = approval?.id;
    }
    if (company?.id) {
      createApprovalInput.companyId = company?.id;
    }
    createApprovalInput.agreedTripNumber = agreedTripNumber || undefined;
    createApprovalInput.issueDate = issueDate || undefined;
    createApprovalInput.validFrom = validFrom || undefined;
    createApprovalInput.validUntil = validUntil || undefined;
    createApprovalInput.issueAddress = issueAddress || undefined;
    createApprovalInput.authorizedRoute = authorizedRoute || undefined;
    createApprovalInput.authorizedGoods = authorizedGoods || undefined;
    try {
      setIsLoading(true);
      if (isAllowed) {
        await ApprovalService.approve(createApprovalInput);
      } else {
        await ApprovalService.create(createApprovalInput);
      }
      resetForm();
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-md p-5">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {approval?.id ? "Update Approval" : "Request Approval"}
            </h3>
            <button
              onClick={() => onClose(true)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form className="mt-3 mb-3" onSubmit={createAction}>
            {isAllowed && (
              <GenericInput
                label={t('number_of_trips_label')}
                value={agreedTripNumber}
                onChange={onAgreedTripNumber}
                type="text"
                placeholder={t('number_of_trips_hint')}
                required={false}
              />
            )}
            {isAllowed && (
              <GenericInput
                label={t('place_of_issue_label')}
                value={issueAddress}
                onChange={onIssueAddress}
                type="text"
                placeholder={t('place_of_issue_hint')}
                required={false}
              />
            )}

            <GenericInput
              label={t('route_label')}
              value={authorizedRoute}
              onChange={onAuthorizedRoute}
              type="text"
              placeholder={t('route_hint')}
              required={false}
            />

            <GenericInput
              label={t('goods_label')}
              value={authorizedGoods}
              onChange={onAuthorizedGoods}
              type="text"
              placeholder={t('goods_hint')}
              required={false}
            />

            {isAllowed && (
              <GenericInput
                label={t('date_of_issue_label')}
                type="date"
                value={issueDate}
                onChange={onIssueDate}
                required={false}
              />
            )}

            {isAllowed && (
              <div className="grid gap-6 mb-6 md:grid-cols-2">
                <GenericInput
                  label={t('valid_from')}
                  type="date"
                  value={validFrom}
                  onChange={onValidFrom}
                  required={false}
                />
                <GenericInput
                  label={t('valid_until')}
                  type="date"
                  value={validUntil}
                  onChange={onValidUntil}
                  required={false}
                />
              </div>
            )}
            <div className="mx-5 mt-6">
              <GenericButton type="submit" disabled={!isFormValid}>
                {t('submit')}
              </GenericButton>
            </div>
          </form>
        </div>
      </div>

      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
